// ═══════════════════════════════════════════════════════
// Auth Context — manages JWT session across the app
// ═══════════════════════════════════════════════════════
import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { auth, setToken, clearToken, getToken, type User } from '../api/client';

interface AuthCtx {
  user:          User | null;
  loading:       boolean;
  login:         (email: string, password: string) => Promise<void>;
  logout:        () => Promise<void>;
  register:      (data: { company_name: string; email: string; password: string; display_name: string }) => Promise<void>;
  hasTier:       (minTier: 'starter' | 'growth' | 'enterprise') => boolean;
  hasFeature:    (flag: string) => boolean;
  isSuperAdmin:  () => boolean;
}

const TIER_RANK: Record<string, number> = { starter: 1, growth: 2, enterprise: 3 };

const Ctx = createContext<AuthCtx>({
  user: null, loading: true,
  login: async () => {}, logout: async () => {}, register: async () => {},
  hasTier: () => false, hasFeature: () => false, isSuperAdmin: () => false,
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user,    setUser]    = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // On mount: verify existing token
  useEffect(() => {
    const token = getToken();
    if (!token) { setLoading(false); return; }
    auth.me()
      .then(setUser)
      .catch(() => clearToken())
      .finally(() => setLoading(false));
  }, []);

  // Listen for forced logout (401 after refresh fails)
  useEffect(() => {
    const handle = () => { setUser(null); clearToken(); };
    window.addEventListener('piq:logout', handle);
    return () => window.removeEventListener('piq:logout', handle);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const data = await auth.login(email, password);
    setToken(data.access_token);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    await auth.logout();
    clearToken();
    setUser(null);
  }, []);

  const register = useCallback(async (data: {
    company_name: string; email: string; password: string; display_name: string;
  }) => {
    const resp = await auth.register(data);
    setToken(resp.access_token);
    setUser(resp.user);
  }, []);

  const hasTier = useCallback((minTier: 'starter' | 'growth' | 'enterprise') => {
    const userTier = user?.tier || 'starter';
    return (TIER_RANK[userTier] ?? 0) >= (TIER_RANK[minTier] ?? 0);
  }, [user]);

  const hasFeature = useCallback((flag: string) => {
    if (!user) return false;
    if (user.tier === 'enterprise') return true;
    return (user.features || []).includes(flag);
  }, [user]);

  const isSuperAdmin = useCallback(() => {
    return user?.role === 'super_admin';
  }, [user]);

  return (
    <Ctx.Provider value={{ user, loading, login, logout, register, hasTier, hasFeature, isSuperAdmin }}>
      {children}
    </Ctx.Provider>
  );
}

export const useAuth = () => useContext(Ctx);
